import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

type AuthState = {
  session: Session | null;
  loading: boolean;
  passwordSetupMode: boolean;
  signInWithPassword: (email:string,password:string)=>Promise<void>;
  requestPasswordReset: (email:string)=>Promise<void>;
  updatePassword: (password:string)=>Promise<void>;
  signOut:()=>Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({children}:{children:ReactNode}) {
  const [session,setSession]=useState<Session|null>(null);
  const [loading,setLoading]=useState(true);
  const [passwordSetupMode,setPasswordSetupMode]=useState(()=>window.location.pathname==='/auth/setup-password');

  useEffect(()=>{
    let active=true;
    supabase.auth.getSession().then(({data})=>{
      if(active){setSession(data.session);setLoading(false)}
    });
    const {data}=supabase.auth.onAuthStateChange((event,next)=>{
      setSession(next);
      if(event==='PASSWORD_RECOVERY'||window.location.pathname==='/auth/setup-password')setPasswordSetupMode(true);
      if(event==='SIGNED_OUT')setPasswordSetupMode(false);
    });
    return()=>{active=false;data.subscription.unsubscribe()};
  },[]);

  const signInWithPassword=async(email:string,password:string)=>{
    const {error}=await supabase.auth.signInWithPassword({email,password});
    if(error)throw error;
  };

  const requestPasswordReset=async(email:string)=>{
    const {error}=await supabase.auth.resetPasswordForEmail(email,{redirectTo:`${window.location.origin}/auth/setup-password`});
    if(error)throw error;
  };

  const updatePassword=async(password:string)=>{
    const {error}=await supabase.auth.updateUser({password});
    if(error)throw error;
    window.history.replaceState({},document.title,'/');
    setPasswordSetupMode(false);
  };

  const signOut=async()=>{await supabase.auth.signOut()};

  return <AuthContext.Provider value={{session,loading,passwordSetupMode,signInWithPassword,requestPasswordReset,updatePassword,signOut}}>{children}</AuthContext.Provider>;
}

export function useAuth(){const value=useContext(AuthContext);if(!value)throw new Error('useAuth must be inside AuthProvider');return value}
